import { Building2, Heart, Flame, TrendingUp, MapPin, Users, Coins } from "lucide-react";
import { storyElement } from "@/lib/story-element";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useLocale } from "@/hooks/useLocale";
import type { Translations } from "@/lib/i18n/types";

interface OrgOverviewPanelProps {
  orgName: string | null;
  orgType: string | null;
  orgTier: number | null;
  orgMorale: number | null;
  orgHeat: number | null;
  orgIncome: number | null;
  controlledLocations: any[] | null;
  managementStyle: string | null;
  orgMembers: Record<string, any> | any[] | null;
  currencyAmount?: number | null;
  currencyName?: string | null;
}

const TIER_LABELS: Record<number, keyof Translations> = {
  1: "play_tier_street",
  2: "play_tier_established",
  3: "play_tier_major",
  4: "play_tier_dominant",
  5: "play_tier_undisputed",
};

const TYPE_LABELS: Record<string, keyof Translations> = {
  criminal: "play_org_criminal",
  intelligence: "play_org_intelligence",
  corporate: "play_org_corporate",
  military: "play_org_military",
  custom: "play_org_custom",
};

const STYLE_LABELS: Record<string, keyof Translations> = {
  hierarchical: "play_style_hierarchical",
  cell: "play_style_cell",
  feudal: "play_style_feudal",
};

export function OrgOverviewPanel({
  orgName, orgType, orgTier, orgMorale, orgHeat, orgIncome, controlledLocations, managementStyle, orgMembers,
  currencyAmount, currencyName,
}: OrgOverviewPanelProps) {
  const { t } = useLocale();
  const tier = orgTier ?? 1;
  const morale = orgMorale ?? 50;
  const heat = orgHeat ?? 0;
  const income = orgIncome ?? 0;
  const locations = controlledLocations || [];

  // Support manpower_roster (Record) or legacy org_members (array)
  const rosterEntries: { name: string; role: string; count: number; status: string; experience?: string }[] = [];
  if (orgMembers && typeof orgMembers === "object" && !Array.isArray(orgMembers)) {
    // manpower_roster format
    for (const [id, batch] of Object.entries(orgMembers)) {
      if (batch && typeof batch === "object") {
        rosterEntries.push({
          name: (batch as any).role || id,
          role: (batch as any).role || "member",
          count: (batch as any).count ?? 0,
          status: (batch as any).status || "active",
          experience: (batch as any).experience,
        });
      }
    }
  } else if (Array.isArray(orgMembers)) {
    for (const m of orgMembers) {
      rosterEntries.push({
        name: m.name || "Unknown",
        role: m.role || "member",
        count: 1,
        status: m.status || "active",
      });
    }
  }

  const activeCount = rosterEntries.reduce((s, r) => s + (r.status === "active" ? r.count : 0), 0);
  const totalCount = rosterEntries.reduce((s, r) => s + r.count, 0);

  return (
    <div {...storyElement("org-overview-panel")} className="space-y-4">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary" />
          <span className="text-sm font-bold text-foreground">{orgName || t.play_org_unnamed}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary" className="text-[10px]">{t[TYPE_LABELS[orgType || ""]] || orgType}</Badge>
          <Badge variant="outline" className="text-[10px]">{t[STYLE_LABELS[managementStyle || ""]] || managementStyle}</Badge>
        </div>
      </div>

      <Separator />

      {/* Treasury */}
      {currencyAmount != null && (
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-muted-foreground">
            <Coins className="w-3 h-3 text-yellow-500" /> {t.play_org_treasury}
          </span>
          <div className="flex items-center gap-2">
            <span className="font-bold text-foreground">{currencyAmount} {currencyName || "Gold"}</span>
            {income > 0 && (
              <span className="text-[10px] text-green-400 flex items-center gap-0.5">
                <TrendingUp className="w-2.5 h-2.5" />+{income}/turn
              </span>
            )}
          </div>
        </div>
      )}

      {currencyAmount == null && income > 0 && (
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-muted-foreground">
            <TrendingUp className="w-3 h-3" /> {t.play_org_income_turn}
          </span>
          <span className="font-medium text-foreground">{income}</span>
        </div>
      )}

      {/* Tier */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{t.play_org_tier}</span>
          <span className="font-bold text-foreground">{tier} — {t[TIER_LABELS[tier]] || `${t.play_org_tier} ${tier}`}</span>
        </div>
        <Progress value={(tier / 5) * 100} className="h-1.5" />
      </div>

      {/* Morale */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-muted-foreground">
            <Heart className={`w-3 h-3 ${morale < 40 ? "text-destructive" : morale > 70 ? "text-green-400" : "text-muted-foreground"}`} />
            Morale
          </span>
          <span className={`font-medium ${morale < 40 ? "text-destructive" : morale > 70 ? "text-green-400" : "text-foreground"}`}>
            {morale}/100
          </span>
        </div>
        <Progress value={morale} className="h-1.5" />
      </div>

      {/* Heat */}
      {heat > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Flame className={`w-3 h-3 ${heat > 60 ? "text-destructive" : heat > 30 ? "text-orange-400" : "text-muted-foreground"}`} />
              Heat
            </span>
            <span className={`font-medium ${heat > 60 ? "text-destructive" : heat > 30 ? "text-orange-400" : "text-foreground"}`}>
              {heat}/100
            </span>
          </div>
          <Progress value={heat} className="h-1.5" />
        </div>
      )}

      {/* Personnel (from manpower_roster) */}
      {rosterEntries.length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <Users className="w-3 h-3" /> {t.play_org_personnel} ({activeCount} / {totalCount})
            </div>
            {rosterEntries.map((r, i) => (
              <div key={r.name + i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 truncate">
                  <span className={`w-1.5 h-1.5 rounded-full ${r.status === "active" ? "bg-green-400" : r.status === "deceased" ? "bg-destructive" : "bg-muted-foreground"}`} />
                  <span className="text-foreground truncate">{r.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="text-[10px]">{r.count}</Badge>
                  {r.experience && (
                    <Badge variant="secondary" className="text-[10px]">{r.experience}</Badge>
                  )}
                  {r.status !== "active" && (
                    <Badge variant="secondary" className="text-[10px]">{r.status}</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {rosterEntries.length === 0 && (
        <div className="text-xs text-muted-foreground/50 text-center py-2">
          {t.play_org_no_personnel}
        </div>
      )}

      {/* Controlled Locations */}
      {locations.length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> {t.play_org_controlled_loc} ({locations.length})
            </div>
            {locations.map((loc: any, i: number) => (
              <div key={loc.id || i} className="flex items-center justify-between text-xs">
                <span className="truncate text-foreground">{loc.name || loc.id}</span>
                {loc.defense_level && (
                  <Badge variant="outline" className="text-[10px]">🛡️ {loc.defense_level}</Badge>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {locations.length === 0 && (
        <div className="text-xs text-muted-foreground/50 text-center py-2">
          {t.play_org_no_territory_ctrl}
        </div>
      )}
    </div>
  );
}
